import { useState, useEffect, useCallback, useRef } from "react";
import { Search, ExternalLink, Loader2, Download, SkipForward, RotateCcw, ChevronLeft, ChevronRight, Eye, X, FileText, Link, Hash } from "lucide-react";
import { toast } from "react-hot-toast";
import webScraperService, {
  WebScrapeDiscoveredItem,
  WebScraperItemType,
  WebScraperItemStatus,
  WebScraperFileType,
} from "../../../services/web-scraper.service";

interface DiscoveredItemsTableProps {
  jobId: string;
  isJobRunning?: boolean;
  refreshKey?: number;
  onImport: (dto: { item_ids?: string[]; import_all?: boolean }) => void;
  onSkip?: () => void;
}

const PAGE_SIZE = 20;

const itemStatusColors: Record<WebScraperItemStatus, string> = {
  [WebScraperItemStatus.DISCOVERED]: "bg-gray-100 text-gray-600",
  [WebScraperItemStatus.IMPORTING]:  "bg-blue-100 text-blue-700 animate-pulse",
  [WebScraperItemStatus.IMPORTED]:   "bg-green-100 text-green-700",
  [WebScraperItemStatus.SKIPPED]:    "bg-yellow-100 text-yellow-700",
  [WebScraperItemStatus.FAILED]:     "bg-red-100 text-red-700",
};

const fileTypeBadgeColors: Partial<Record<WebScraperFileType, string>> = {
  [WebScraperFileType.PDF]:  "bg-red-100 text-red-700",
  [WebScraperFileType.DOCX]: "bg-blue-100 text-blue-700",
  [WebScraperFileType.XLSX]: "bg-green-100 text-green-700",
  [WebScraperFileType.PPTX]: "bg-orange-100 text-orange-700",
  [WebScraperFileType.CSV]:  "bg-teal-100 text-teal-700",
  [WebScraperFileType.IMAGE]:"bg-purple-100 text-purple-700",
  [WebScraperFileType.ZIP]:  "bg-gray-100 text-gray-700",
};

function formatBytes(bytes: number | null | undefined): string {
  if (bytes == null || !Number.isFinite(bytes) || bytes <= 0) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1_048_576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1_048_576).toFixed(2)} MB`;
}

function truncateUrl(url: string, max = 40): string {
  return url.length > max ? url.slice(0, max) + "…" : url;
}

export default function DiscoveredItemsTable({ jobId, isJobRunning, refreshKey, onImport, onSkip }: DiscoveredItemsTableProps) {
  const [items, setItems] = useState<WebScrapeDiscoveredItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  const [itemTypeFilter, setItemTypeFilter] = useState<WebScraperItemType | "">("");
  const [statusFilter, setStatusFilter] = useState<WebScraperItemStatus | "">("");
  const [fileTypeFilter, setFileTypeFilter] = useState<WebScraperFileType | "">("");
  const [searchTerm, setSearchTerm] = useState("");

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [viewingItem, setViewingItem] = useState<WebScrapeDiscoveredItem | null>(null);

  // Keep filters in a ref so fetchItems stays stable (avoids double-fetch when filters change)
  const filtersRef = useRef({ itemTypeFilter, statusFilter, fileTypeFilter, searchTerm });
  filtersRef.current = { itemTypeFilter, statusFilter, fileTypeFilter, searchTerm };
  const initialFetchDone = useRef(false);

  const fetchItems = useCallback(async (page: number) => {
    const { itemTypeFilter: itf, statusFilter: sf, fileTypeFilter: ftf, searchTerm: st } = filtersRef.current;
    setLoading(true);
    try {
      const result = await webScraperService.listItems(jobId, {
        ...(itf ? { item_type: itf } : {}),
        ...(sf ? { status: sf } : {}),
        ...(ftf ? { file_type: ftf } : {}),
        ...(st ? { search: st } : {}),
        skip: (page - 1) * PAGE_SIZE,
        limit: PAGE_SIZE,
      });

      // Backend returns empty when no item_type filter is sent.
      // Fall back to FILE tab so data is visible after job completes.
      if (result.total === 0 && !itf && !sf && !ftf && !st) {
        const fileResult = await webScraperService.listItems(jobId, {
          item_type: WebScraperItemType.FILE,
          skip: (page - 1) * PAGE_SIZE,
          limit: PAGE_SIZE,
        });
        if (fileResult.total > 0) {
          setItemTypeFilter(WebScraperItemType.FILE);
          setItems(fileResult.list);
          setTotal(fileResult.total);
          return;
        }
      }

      setItems(result.list);
      setTotal(result.total);
    } catch {
      toast.error("Failed to load items");
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  // Filter changes: skip on mount (page effect handles initial fetch),
  // then reset selection and either fetch page 1 directly or reset page (which triggers page effect)
  useEffect(() => {
    if (!initialFetchDone.current) return;
    setSelectedIds(new Set());
    if (currentPage !== 1) {
      setCurrentPage(1);
    } else {
      fetchItems(1);
    }
  }, [itemTypeFilter, statusFilter, fileTypeFilter, searchTerm]);

  // Initial fetch + page change + explicit refresh
  useEffect(() => {
    initialFetchDone.current = true;
    fetchItems(currentPage);
  }, [fetchItems, currentPage, refreshKey]);

  // All three item kinds are importable on the backend (PAGE items get
  // converted to .txt during import), so let the user select any of them.
  const isSelectable = (item: WebScrapeDiscoveredItem) =>
    item.item_type === WebScraperItemType.FILE
    || item.item_type === WebScraperItemType.IMAGE
    || item.item_type === WebScraperItemType.PAGE;

  const selectableItems = items.filter(isSelectable);
  const allSelected = selectableItems.length > 0 && selectableItems.every((i) => selectedIds.has(i._id));

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(selectableItems.map((i) => i._id)));
    }
  };

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleSkipSelected = async () => {
    if (selectedIds.size === 0) return;
    setActionLoading("skip");
    try {
      await webScraperService.skipItems(jobId, [...selectedIds]);
      toast.success(`${selectedIds.size} item(s) skipped`);
      setSelectedIds(new Set());
      fetchItems(currentPage);
      onSkip?.();
    } catch {
      toast.error("Failed to skip items");
    } finally {
      setActionLoading(null);
    }
  };

  const handleUndoSkip = async (item: WebScrapeDiscoveredItem) => {
    setActionLoading(item._id);
    try {
      const result = await webScraperService.undoSkipItems(jobId, [item._id]);
      if (result.action === "imported") {
        toast.success(`${result.imported} item(s) imported into the document store`);
        if (result.failed > 0) {
          toast.error(`${result.failed} item(s) failed to import — check the items list for details`);
        }
      } else {
        toast.success("Item restored — ready for review");
      }
      fetchItems(currentPage);
      onSkip?.();
    } catch {
      toast.error("Failed to undo skip");
    } finally {
      setActionLoading(null);
    }
  };

  const handleSkipSingle = async (item: WebScrapeDiscoveredItem) => {
    setActionLoading(item._id);
    try {
      await webScraperService.skipItems(jobId, [item._id]);
      toast.success("Item skipped");
      fetchItems(currentPage);
      onSkip?.();
    } catch {
      toast.error("Failed to skip item");
    } finally {
      setActionLoading(null);
    }
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div className="bg-white rounded-xl border border-gray-200">
      {/* Section header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
        <h3 className="text-sm font-semibold text-gray-900">
          Discovered Items {total > 0 && <span className="text-gray-400 font-normal">({total})</span>}
        </h3>
        {loading ? (
          <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
        ) : isJobRunning ? (
          <span className="flex items-center gap-1.5 text-xs text-blue-500 font-medium">
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
            Crawling…
          </span>
        ) : null}
      </div>

      {/* Filters */}
      <div className={`flex flex-wrap items-center gap-3 px-5 py-3 border-b border-gray-100 bg-gray-50 ${isJobRunning ? "opacity-50 pointer-events-none select-none" : ""}`}>
        {/* Item type tabs */}
        <div className="flex gap-1 bg-white border border-gray-200 rounded-lg p-1">
          {(["", "FILE", "PAGE", "IMAGE"] as const).map((t) => (
            <button
              key={t}
              onClick={() => setItemTypeFilter(t as WebScraperItemType | "")}
              disabled={isJobRunning}
              className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                itemTypeFilter === t ? "bg-purple-600 text-white" : "text-gray-600 hover:bg-gray-100"
              }`}
              type="button"
            >
              {t || "All"}
            </button>
          ))}
        </div>

        {/* Status filter */}
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as WebScraperItemStatus | "")}
          disabled={isJobRunning}
          className="px-3 py-1.5 border border-gray-200 rounded-lg text-xs text-gray-600 bg-white focus:outline-none focus:ring-2 focus:ring-purple-400 disabled:cursor-not-allowed"
        >
          <option value="">All Statuses</option>
          {Object.values(WebScraperItemStatus).map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>

        {/* File type filter */}
        <select
          value={fileTypeFilter}
          onChange={(e) => setFileTypeFilter(e.target.value as WebScraperFileType | "")}
          disabled={isJobRunning}
          className="px-3 py-1.5 border border-gray-200 rounded-lg text-xs text-gray-600 bg-white focus:outline-none focus:ring-2 focus:ring-purple-400 disabled:cursor-not-allowed"
        >
          <option value="">All File Types</option>
          {Object.values(WebScraperFileType).map((ft) => (
            <option key={ft} value={ft}>{ft}</option>
          ))}
        </select>

        {/* Search */}
        <div className="relative ml-auto">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400" />
          <input
            type="text"
            placeholder="Search name or title…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            disabled={isJobRunning}
            className="pl-7 pr-3 py-1.5 border border-gray-200 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-purple-400 w-48 disabled:cursor-not-allowed"
          />
        </div>
      </div>

      {/* Bulk action bar */}
      {selectedIds.size > 0 && (
        <div className="flex items-center gap-3 px-5 py-2.5 bg-purple-50 border-b border-purple-100">
          <span className="text-xs font-medium text-purple-700">{selectedIds.size} selected</span>
          <button
            onClick={() => onImport({ item_ids: [...selectedIds] })}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-medium hover:bg-blue-700 transition-colors"
            type="button"
          >
            <Download className="w-3 h-3" />
            Import Selected
          </button>
          <button
            onClick={handleSkipSelected}
            disabled={!!actionLoading}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-yellow-500 text-white rounded-lg text-xs font-medium hover:bg-yellow-600 transition-colors disabled:opacity-50"
            type="button"
          >
            {actionLoading === "skip" ? <Loader2 className="w-3 h-3 animate-spin" /> : <SkipForward className="w-3 h-3" />}
            Skip Selected
          </button>
          <button
            onClick={() => setSelectedIds(new Set())}
            className="text-xs text-gray-500 hover:text-gray-700 ml-auto"
            type="button"
          >
            Clear selection
          </button>
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-100 bg-gray-50">
              <th className="w-10 px-4 py-3">
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={toggleSelectAll}
                  className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                />
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Name / Title</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Type</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Source URL</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Depth</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Size</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {loading && items.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-4 py-12 text-center">
                  <Loader2 className="w-6 h-6 animate-spin text-gray-400 mx-auto" />
                </td>
              </tr>
            ) : items.length === 0 && isJobRunning ? (
              <tr>
                <td colSpan={8} className="px-4 py-16 text-center">
                  <Loader2 className="w-7 h-7 animate-spin text-blue-400 mx-auto mb-3" />
                  <p className="text-sm font-medium text-blue-600">Crawling in progress</p>
                  <p className="text-xs text-gray-400 mt-1">Discovered items will appear here automatically</p>
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-4 py-12 text-center text-sm text-gray-400">
                  No items found
                </td>
              </tr>
            ) : (
              items.map((item) => {
                const canSelect = isSelectable(item);
                const isSelected = selectedIds.has(item._id);
                const isPage = item.item_type === WebScraperItemType.PAGE;

                return (
                  <tr
                    key={item._id}
                    className={`hover:bg-gray-50 transition-colors ${isSelected ? "bg-purple-50" : ""}`}
                  >
                    {/* Checkbox */}
                    <td className="px-4 py-3">
                      {canSelect ? (
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleSelect(item._id)}
                          className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                        />
                      ) : (
                        <span className="block w-4" />
                      )}
                    </td>

                    {/* Name / Title */}
                    <td className="px-4 py-3 max-w-[200px]">
                      <span className="text-gray-900 font-medium text-xs truncate block" title={item.file_name || item.page_title || "—"}>
                        {item.file_name || item.page_title || "—"}
                      </span>
                      {isPage && item.page_text_snippet && (
                        <span className="text-gray-400 text-xs truncate block mt-0.5" title={item.page_text_snippet}>
                          {item.page_text_snippet.length > 60 ? item.page_text_snippet.slice(0, 60) + "…" : item.page_text_snippet}
                        </span>
                      )}
                    </td>

                    {/* Type */}
                    <td className="px-4 py-3">
                      {item.file_type ? (
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${fileTypeBadgeColors[item.file_type] || "bg-gray-100 text-gray-600"}`}>
                          {item.file_type}
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-600">
                          {item.item_type}
                        </span>
                      )}
                    </td>

                    {/* Source URL */}
                    <td className="px-4 py-3 max-w-[180px]">
                      <a
                        href={item.source_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 text-xs text-blue-600 hover:underline truncate"
                        title={item.source_url}
                      >
                        {truncateUrl(item.source_url)}
                        <ExternalLink className="w-3 h-3 flex-shrink-0" />
                      </a>
                    </td>

                    {/* Depth */}
                    <td className="px-4 py-3 text-xs text-gray-500">{item.depth}</td>

                    {/* Size */}
                    <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">{formatBytes(item.file_size_bytes)}</td>

                    {/* Status */}
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${itemStatusColors[item.status] || "bg-gray-100 text-gray-600"}`}>
                        {item.status}
                      </span>
                    </td>

                    {/* Action */}
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {isPage && (
                          <button
                            onClick={() => setViewingItem(item)}
                            className="flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-700"
                            type="button"
                            title="View scraped data"
                          >
                            <Eye className="w-3 h-3" />
                            View
                          </button>
                        )}
                        {canSelect && item.status === WebScraperItemStatus.SKIPPED && (
                          <button
                            onClick={() => handleUndoSkip(item)}
                            disabled={!!actionLoading}
                            className="flex items-center gap-1 text-xs text-green-600 hover:text-green-700 disabled:opacity-50"
                            type="button"
                            title="Undo Skip"
                          >
                            {actionLoading === item._id ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />}
                            Undo
                          </button>
                        )}
                        {canSelect && item.status === WebScraperItemStatus.DISCOVERED && (
                          <button
                            onClick={() => handleSkipSingle(item)}
                            disabled={!!actionLoading}
                            className="flex items-center gap-1 text-xs text-yellow-600 hover:text-yellow-700 disabled:opacity-50"
                            type="button"
                            title="Skip this item"
                          >
                            {actionLoading === item._id ? <Loader2 className="w-3 h-3 animate-spin" /> : <SkipForward className="w-3 h-3" />}
                            Skip
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between px-5 py-3 border-t border-gray-100">
          <span className="text-xs text-gray-500">
            {(currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, total)} of {total}
          </span>
          <div className="flex gap-1">
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="p-1.5 rounded hover:bg-gray-100 disabled:opacity-40 transition-colors"
              type="button"
            >
              <ChevronLeft className="w-4 h-4 text-gray-600" />
            </button>
            <span className="px-3 py-1 text-xs text-gray-600">{currentPage} / {totalPages}</span>
            <button
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="p-1.5 rounded hover:bg-gray-100 disabled:opacity-40 transition-colors"
              type="button"
            >
              <ChevronRight className="w-4 h-4 text-gray-600" />
            </button>
          </div>
        </div>
      )}

      {/* Page Data View Modal */}
      {viewingItem && (
        <div
          className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4 z-50"
          onClick={() => setViewingItem(null)}
        >
          <div
            className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal header */}
            <div className="flex items-start justify-between p-5 border-b border-gray-200 flex-shrink-0">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-9 h-9 bg-indigo-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <FileText className="w-4 h-4 text-indigo-600" />
                </div>
                <div className="min-w-0">
                  <h2 className="text-sm font-bold text-gray-900 truncate">
                    {viewingItem.page_title || "Untitled Page"}
                  </h2>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium mt-0.5 ${itemStatusColors[viewingItem.status] || "bg-gray-100 text-gray-600"}`}>
                    {viewingItem.status}
                  </span>
                </div>
              </div>
              <button
                onClick={() => setViewingItem(null)}
                className="p-1.5 text-gray-400 hover:bg-gray-100 rounded-full transition-colors flex-shrink-0 ml-2"
                type="button"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Modal body */}
            <div className="flex-1 overflow-y-auto p-5 space-y-4">

              {/* OG Image */}
              {viewingItem.meta_og_image && (
                <div>
                  <img
                    src={viewingItem.meta_og_image}
                    alt="OG preview"
                    className="w-full max-h-48 object-cover rounded-lg border border-gray-200"
                    onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
                  />
                </div>
              )}

              {/* URLs */}
              <div className="space-y-2">
                <div className="flex items-start gap-2">
                  <Link className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="text-xs font-medium text-gray-500 mb-0.5">Source URL</p>
                    <a
                      href={viewingItem.source_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-blue-600 hover:underline break-all"
                    >
                      {viewingItem.source_url}
                    </a>
                  </div>
                </div>
                {viewingItem.found_on_page_url && (
                  <div className="flex items-start gap-2">
                    <Link className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-xs font-medium text-gray-500 mb-0.5">Found On</p>
                      <a
                        href={viewingItem.found_on_page_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-blue-600 hover:underline break-all"
                      >
                        {viewingItem.found_on_page_url}
                      </a>
                    </div>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <Hash className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  <div>
                    <p className="text-xs font-medium text-gray-500 mb-0.5">Depth</p>
                    <p className="text-xs text-gray-700">{viewingItem.depth}</p>
                  </div>
                </div>
              </div>

              <hr className="border-gray-100" />

              {/* Meta fields */}
              <div className="space-y-3">
                {viewingItem.page_title && (
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Page Title</p>
                    <p className="text-sm text-gray-900">{viewingItem.page_title}</p>
                  </div>
                )}
                {viewingItem.meta_og_title && viewingItem.meta_og_title !== viewingItem.page_title && (
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">OG Title</p>
                    <p className="text-sm text-gray-900">{viewingItem.meta_og_title}</p>
                  </div>
                )}
                {viewingItem.meta_description && (
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Meta Description</p>
                    <p className="text-sm text-gray-700">{viewingItem.meta_description}</p>
                  </div>
                )}
                {(viewingItem.page_full_text || viewingItem.page_text_snippet) && (
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                        {viewingItem.page_full_text ? "Full Page Text" : "Text Snippet"}
                      </p>
                      <span className="text-xs text-gray-400">
                        {(viewingItem.page_full_text || viewingItem.page_text_snippet)!.length.toLocaleString()} chars
                      </span>
                    </div>
                    <pre className="text-xs text-gray-700 leading-relaxed whitespace-pre-wrap bg-gray-50 rounded-lg p-3 border border-gray-100 max-h-64 overflow-y-auto font-sans">
                      {viewingItem.page_full_text || viewingItem.page_text_snippet}
                    </pre>
                  </div>
                )}
                {!viewingItem.page_title && !viewingItem.meta_description && !viewingItem.page_full_text && !viewingItem.page_text_snippet && (
                  <p className="text-sm text-gray-400 italic text-center py-4">No metadata available for this page</p>
                )}
              </div>
            </div>

            {/* Modal footer */}
            <div className="flex justify-between items-center px-5 py-3 border-t border-gray-100 bg-gray-50 flex-shrink-0">
              <a
                href={viewingItem.source_url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 text-xs text-blue-600 hover:underline"
              >
                <ExternalLink className="w-3 h-3" />
                Open page in new tab
              </a>
              <button
                onClick={() => setViewingItem(null)}
                className="px-3 py-1.5 bg-gray-900 text-white rounded-lg text-xs font-medium hover:bg-gray-700 transition-colors"
                type="button"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
